/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { readFileSync, readdirSync } from 'fs';
import get from 'lodash/get';
import flatMap from 'lodash/flatMap';
import pathData from '../../buildArtifacts/paths';
import getSettings from '../../buildArtifacts/settings';
import Layout from '../../components/Layout';
import PageVideo, { PageVideoPropTypes } from '../../components/PageVideo';
import tidySeo from '../../utils/tidySeo';
import getSeoPageTitle from '../../utils/getSeoPageTitle';
import getCoveoSearchMeta from '../../utils/getCoveoSearchMeta';
import Breadcrumb from '../../components/Breadcrumb';
import getBreadcrumb from "../../utils/getBreadcrumb";

const { pageVideo: paths } = pathData;

export const VideoPropTypes = {
  pageData: PropTypes.shape(PageVideoPropTypes).isRequired,
  pageTitle: PropTypes.string.isRequired,
  seo: PropTypes.object,
  canonicalUrl: PropTypes.string.isRequired,
  settingsGlobal: PropTypes.object.isRequired,
  contentSections: PropTypes.array
};

export const VideoPage = ({ pageData, seo = {}, pageTitle, breadcrumbs, canonicalUrl, settingsGlobal, contentSections = [], nextSlug, params, videos, IndexedVideosOnly }) => {
  const {
    _id,
    _contentTypeId,
    _as,
    _href,
    slug,
    pageTitle: videoPageTitle,
    video,
    videoTitle,
    videoSummary,
    transcript,
    wistiaVideo,
    subjects,
    tags,
    videoCardImage
  } = pageData;
  return (
    <Layout
      seo={seo}
      pageTitle={pageTitle ?? videoPageTitle}
      canonicalUrl={canonicalUrl}
      showFooter
      settingsGlobal={settingsGlobal}>
      <Breadcrumb breadcrumbs={breadcrumbs} parentPage="pageVideo" />

      <PageVideo
        _id={_id}
        _contentTypeId={_contentTypeId}
        _as={_as}
        _href={_href}
        slug={slug}
        pageTitle={videoPageTitle}
        video={video}
        videoTitle={videoTitle}
        videoSummary={videoSummary}
        transcript={transcript}
        contentSections={contentSections}
        wistiaVideo={wistiaVideo}
        nextSlug={nextSlug}
        videoCardImage={videoCardImage}
        subjects={subjects}
        tags={tags}
        IndexedVideosOnly={IndexedVideosOnly}
        videos={videos}
      />
    </Layout>
  );
};

export const getStaticPaths = async ({ locales }) => ({
  paths: flatMap(locales, (locale) => paths.map((pathObject) => ({ ...pathObject, locale }))),
  fallback: false
});

export const getStaticProps = async ({ locale, params }) => {
  const settingsGlobal = getSettings(locale);
  const { videoslug: slug } = params;

  const pageData = JSON.parse(
    readFileSync(`./src/buildArtifacts/contentJson/${locale}/pageVideo/${slug}.json`, 'utf8')
  );

  const files = readdirSync(`./src/buildArtifacts/contentJson/${locale}/pageVideo/`)
  const videos =
    files?.length &&
    files?.map((file) =>
      JSON.parse(readFileSync(`./src/buildArtifacts/contentJson/${locale}/pageVideo/${file}`, 'utf8'))
    );
  const IndexedVideosOnly = videos?.filter((vid) => {
    const robots = vid?.seo?.robots?.value?.toLowerCase() || "";
    return robots.split(",").map((s) => s.trim()).includes("index");
  });
  const currentIndex = files.indexOf(`${slug}.json`);
  const nextSlug = currentIndex < files.length - 1 ? files[currentIndex + 1].replace('.json', '') : null;

  const pageDataSeo = tidySeo(get(pageData, 'seo', {}));
  const pageTitle = getSeoPageTitle(pageDataSeo, pageData);
  delete pageDataSeo.title;

  const pageCoveoSearchMeta = getCoveoSearchMeta(pageData, locale);
  delete pageCoveoSearchMeta['tag:undefined'];

  const seo = { ...pageDataSeo, ...pageCoveoSearchMeta };
  seo.content_type = { name: 'content_type', value: pageData._contentTypeId };
  seo.lang = { name: 'lang', value: locale };

  const canonicalLocale = locale === 'en-US' ? '' : `${locale}/`;
  const canonicalUrl = seo?.canonical?.value ?? `https://learn.dropbox.com/${canonicalLocale}video-library/${slug}`;

  const allVideos = JSON.parse(readFileSync(`./src/buildArtifacts/custom/videos.json`, 'utf8'));

  if (pageData.video?.videoId) {
    const videoId = pageData.video?.videoId;

    const currentVideo = allVideos.find((video) => video.id === videoId || video.hashed_id === videoId);
    if (currentVideo) {
      pageData.wistiaVideo = {
        success: true,
        video: currentVideo
      };
    } else {
      pageData.wistiaVideo = {
        success: false
      };
    }
  }

  const file = readdirSync(`./src/buildArtifacts/contentJson/${locale}/pageVideoLanding/`)[0];

  const pageDataLanding = JSON.parse(
    readFileSync(`./src/buildArtifacts/contentJson/${locale}/pageVideoLanding/${file}`, 'utf8')
  );

  const { contentSections } = pageDataLanding;
  const pagebreadcrumb = {
    as: "/video-library",
    href: "/video-library",
    text: "Video library"
  }
  const breadcrumbs = [pagebreadcrumb, getBreadcrumb(pageData)];

  return {
    props: {
      pageData,
      seo,
      pageTitle,
      canonicalUrl,
      settingsGlobal,
      contentSections,
      nextSlug,
      breadcrumbs,
      params,
      videos,
      IndexedVideosOnly
    }
  };
};

VideoPage.propTypes = VideoPropTypes;


export default VideoPage;